import { Component, Show, createSignal } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { Button, Input } from "../ui";
import { createProject } from "../../api/projects";
import type { Project, TenantRole } from "../../api/types";
import { canCreateProject } from "../../utils/permissions";
import { GitHubWizard, type LinkedRepoInfo } from "./GitHubWizard";
import { GuidedCreateStep } from "./GuidedCreateStep";
import { OnboardingAnalyzeStep } from "./OnboardingAnalyzeStep";
import { OnboardingImportDocsStep } from "./OnboardingImportDocsStep";
import { logoSrc } from "../../stores/themeStore";
import { clearOnboardingInFlight } from "../../stores/onboardingResume";

export interface WelcomeWizardProps {
  onProjectCreated: (project: Project) => void;
  tenantRole: TenantRole | null;
  /**
   * A project whose onboarding is being picked back up after the GitHub App
   * install round trip (FLOW-991). Present means the create step already
   * happened: the wizard opens at the GitHub step with this project and never
   * calls `createProject`.
   */
  resumeProject?: Project;
}

type WizardStep =
  | "welcome"
  | "create"
  | "github"
  | "analyze"
  | "import-docs"
  | "first-item";

const ONBOARDING_KEY = "flowicity_onboarding_completed";

export const WelcomeWizard: Component<WelcomeWizardProps> = (props) => {
  const navigate = useNavigate();

  const seenBefore = localStorage.getItem(ONBOARDING_KEY) === "true";
  const [step, setStep] = createSignal<WizardStep>(
    props.resumeProject ? "github" : seenBefore ? "create" : "welcome",
  );

  // The marker is consumed here, the one place that proves the resume
  // actually happened — clearing it in the shell would strand a resume that
  // never rendered, and clearing it unconditionally would break an install
  // round trip still in flight elsewhere.
  if (props.resumeProject) {
    clearOnboardingInFlight();
  }

  const [name, setName] = createSignal("");
  const [description, setDescription] = createSignal("");
  const [prefix, setPrefix] = createSignal("");
  const [creating, setCreating] = createSignal(false);
  const [error, setError] = createSignal("");

  // Hold the created project until onboarding finishes
  const [createdProject, setCreatedProject] = createSignal<Project | null>(
    props.resumeProject ?? null,
  );
  const [linkedRepoInfo, setLinkedRepoInfo] =
    createSignal<LinkedRepoInfo | null>(null);

  const handleGetStarted = () => {
    localStorage.setItem(ONBOARDING_KEY, "true");
    setStep("create");
  };

  const handleCreate = async () => {
    const projectName = name().trim();
    if (!projectName) {
      setError("Project name is required");
      return;
    }
    setCreating(true);
    setError("");
    try {
      const project = await createProject({
        name: projectName,
        description: description().trim() || undefined,
        prefix: prefix().trim() || undefined,
      });
      setCreatedProject(project);
      setStep("github");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  const goToAnalyze = (repoInfo?: LinkedRepoInfo) => {
    if (repoInfo) setLinkedRepoInfo(repoInfo);
    setStep("analyze");
  };

  const goToImportDocs = () => {
    setStep("import-docs");
  };

  const goToFirstItem = () => {
    setStep("first-item");
  };

  const finishOnboarding = () => {
    const project = createdProject();
    if (project) {
      props.onProjectCreated(project);
      navigate("/plan");
    }
  };

  return (
    <>
      <Show when={step() === "welcome"}>
        <div class="h-full flex items-center justify-center p-8">
          <div class="max-w-lg text-center">
            <div class="mb-6">
              <img
                src={logoSrc()}
                alt="Flowicity"
                class="w-16 h-16 rounded-md object-contain mx-auto mb-4"
              />
              <h1 class="text-2xl font-bold text-ink mb-2">
                Welcome to Flowicity
              </h1>
              <p class="text-ink-2">
                AI-native project management that reduces cognitive load.
                Organize your work with boards, backlogs, and an AI assistant
                that understands your project.
              </p>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 text-left">
              <div class="bg-surface rounded border border-line p-4">
                <div class="w-8 h-8 bg-sunken rounded flex items-center justify-center mb-2">
                  <svg
                    class="w-4 h-4 text-ink-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7"
                    />
                  </svg>
                </div>
                <h3 class="text-sm font-semibold text-ink mb-1">
                  Kanban Boards
                </h3>
                <p class="text-xs text-ink-3">
                  Visualize work across customizable columns
                </p>
              </div>

              <div class="bg-surface rounded border border-line p-4">
                <div class="w-8 h-8 bg-signal-magenta-tint rounded flex items-center justify-center mb-2">
                  <svg
                    class="w-4 h-4 text-signal-magenta"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                </div>
                <h3 class="text-sm font-semibold text-ink mb-1">
                  AI Assistant
                </h3>
                <p class="text-xs text-ink-3">
                  Chat-driven workflows with smart proposals
                </p>
              </div>

              <div class="bg-surface rounded border border-line p-4">
                <div class="w-8 h-8 bg-sunken rounded flex items-center justify-center mb-2">
                  <svg
                    class="w-4 h-4 text-ink-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <h3 class="text-sm font-semibold text-ink mb-1">
                  Epics & Backlog
                </h3>
                <p class="text-xs text-ink-3">
                  Plan and prioritize with hierarchy and filters
                </p>
              </div>
            </div>

            <Button variant="primary" size="lg" onClick={handleGetStarted}>
              Get Started
            </Button>
            <p class="mt-3 text-xs text-ink-3">
              By continuing, you accept the{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                class="text-ink-2 hover:text-ink underline underline-offset-2"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                class="text-ink-2 hover:text-ink underline underline-offset-2"
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>
      </Show>

      <Show when={step() === "create"}>
        <div class="h-full flex items-center justify-center p-8">
          <div class="w-full max-w-md">
            <Show
              when={canCreateProject(props.tenantRole)}
              fallback={
                <div class="bg-surface rounded border border-line p-6 text-center">
                  <h1 class="text-2xl font-bold text-ink mb-2">
                    Waiting on a project
                  </h1>
                  <p class="text-ink-2">
                    Only an organization admin can create a project. You'll see
                    your work here as soon as an admin creates one or adds you
                    to an existing project.
                  </p>
                </div>
              }
            >
              <div class="text-center mb-6">
                <h1 class="text-2xl font-bold text-ink mb-2">
                  Create your first project
                </h1>
                <p class="text-ink-2">
                  A project is where your team's work lives. You can create more
                  later.
                </p>
              </div>

              <div class="bg-surface rounded border border-line p-6 space-y-4">
                <Input
                  label="Project Name"
                  placeholder="e.g. My App, Backend API"
                  value={name()}
                  onInput={setName}
                  maxLength={255}
                />
                <Input
                  label="Description"
                  placeholder="What is this project about? (optional)"
                  value={description()}
                  onInput={setDescription}
                  isTextarea
                  rows={3}
                />
                <Input
                  label="Prefix"
                  placeholder="e.g. APP (auto-derived if empty)"
                  value={prefix()}
                  onInput={setPrefix}
                  maxLength={10}
                />
                <Show when={error()}>
                  <p class="text-sm text-signal-red">{error()}</p>
                </Show>
                <div class="pt-2">
                  <Button
                    variant="primary"
                    class="w-full"
                    onClick={handleCreate}
                    loading={creating()}
                    disabled={creating()}
                  >
                    Create Project
                  </Button>
                </div>
              </div>
            </Show>
          </div>
        </div>
      </Show>

      <Show when={step() === "github" ? createdProject() : null}>
        {(project) => (
          <GitHubWizard
            projectId={project().id}
            onComplete={goToAnalyze}
            onSkip={() => goToAnalyze()}
          />
        )}
      </Show>

      <Show when={step() === "analyze" ? createdProject() : null}>
        {(project) => (
          <OnboardingAnalyzeStep
            projectId={project().id}
            repoId={linkedRepoInfo()?.id}
            repoOwner={linkedRepoInfo()?.owner}
            repoName={linkedRepoInfo()?.name}
            onComplete={goToImportDocs}
            onSkip={goToImportDocs}
          />
        )}
      </Show>

      <Show when={step() === "import-docs" ? createdProject() : null}>
        {(project) => (
          <OnboardingImportDocsStep
            projectId={project().id}
            onComplete={goToFirstItem}
            onSkip={goToFirstItem}
          />
        )}
      </Show>

      <Show when={step() === "first-item" ? createdProject() : null}>
        {(project) => (
          <GuidedCreateStep
            projectId={project().id}
            onComplete={finishOnboarding}
            onSkip={finishOnboarding}
          />
        )}
      </Show>
    </>
  );
};
